<template>
  <div class="products">
    <el-tabs v-model="activeTab" type="border-card" class="products-tabs" @tab-click="handleTabChange">
      <el-tab-pane label="📦 商品列表" name="list">
        <div class="table-container">
          <div class="toolbar">
            <div class="toolbar-left">
              <el-input v-model="searchKeyword" placeholder="搜索商品名称/ID" prefix-icon="el-icon-search" size="small" style="width: 220px;" clearable @clear="fetchProducts" />
              <el-select v-model="filterCateId" placeholder="全部分类" size="small" style="width: 140px;" clearable @change="fetchProducts">
                <el-option v-for="c in categoryOptions" :key="c.id" :label="c.name" :value="c.id" />
              </el-select>
            </div>
            <div class="toolbar-right">
              <el-button type="danger" size="small" icon="el-icon-plus" @click="openAddDialog">新增商品</el-button>
              <el-button size="small" icon="el-icon-upload2" @click="handleBatchUpload">批量上传</el-button>
            </div>
          </div>

          <div class="batch-bar">
            <el-checkbox v-model="selectAll" @change="handleSelectAll">全选</el-checkbox>
            <el-divider direction="vertical" />
            <el-button size="mini" :disabled="selectedIds.length === 0" @click="batchAction('on')">批量上架</el-button>
            <el-button size="mini" :disabled="selectedIds.length === 0" @click="batchAction('off')">批量下架</el-button>
            <el-button size="mini" type="danger" :disabled="selectedIds.length === 0" @click="batchAction('destroy')">批量删除</el-button>
          </div>

          <el-table ref="productTable" :data="productList" style="width: 100%;" @selection-change="handleSelectionChange" border stripe v-loading="productLoading">
            <el-table-column type="selection" width="45" />
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column label="商品图" width="80">
              <template slot-scope="scope">
                <el-image :src="scope.row.image" style="width: 48px; height: 48px; border-radius: 4px;" fit="cover" />
              </template>
            </el-table-column>
            <el-table-column prop="name" label="商品名" min-width="180" />
            <el-table-column prop="cost" label="成本价" width="100" />
            <el-table-column label="售价" width="100">
              <template slot-scope="scope">
                <span class="price-red">¥{{ scope.row.price }}</span>
              </template>
            </el-table-column>
            <el-table-column label="原价" width="100">
              <template slot-scope="scope">
                <span class="price-original">¥{{ scope.row.originalPrice }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="category" label="分类" width="80" />
            <el-table-column label="商品供应商" min-width="120">
              <template slot-scope="scope">
                <span>{{ scope.row.puhui_admin_name || '平台' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="stock" label="库存" width="80" />
            <el-table-column label="状态" width="100">
              <template slot-scope="scope">
                <el-tag :type="scope.row.status === '已上架' ? 'success' : scope.row.stock <= 50 && scope.row.status === '已上架' ? 'warning' : 'info'" size="small">
                  {{ scope.row.stock <= 50 && scope.row.status === '已上架' ? '即将售罄' : scope.row.status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="220" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" size="small" @click="handleEdit(scope.row)">编辑</el-button>
                <el-button type="text" size="small" @click="openVirtualSales(scope.row)">虚拟销量</el-button>
                <el-button type="text" size="small" :style="{ color: scope.row.status === '已上架' ? '#909399' : '#67c23a' }" @click="handleToggleStatus(scope.row)">
                  {{ scope.row.status === '已上架' ? '下架' : '上架' }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination-wrap">
            <span>共 {{ productTotal }} 条记录</span>
            <el-pagination
              background
              layout="prev, pager, next"
              :total="productTotal"
              :page-size="productLimit"
              :current-page.sync="productPage"
              @current-change="fetchProducts"
            />
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane label="📁 分类管理" name="categories">
        <el-row :gutter="24" class="cat-stats">
          <el-col :span="8">
            <el-card shadow="never" class="cat-stat-card cat-stat-red">
              <p class="cat-stat-label">全部分类</p>
              <h3 class="cat-stat-value">{{ categoryTotal }}</h3>
            </el-card>
          </el-col>
          <el-col :span="8">
            <el-card shadow="never" class="cat-stat-card cat-stat-green">
              <p class="cat-stat-label">已启用分类</p>
              <h3 class="cat-stat-value">{{ categoryEnabledCount }}</h3>
            </el-card>
          </el-col>
          <el-col :span="8">
            <el-card shadow="never" class="cat-stat-card cat-stat-gray">
              <p class="cat-stat-label">已禁用分类</p>
              <h3 class="cat-stat-value">{{ categoryTotal - categoryEnabledCount }}</h3>
            </el-card>
          </el-col>
        </el-row>

        <div class="toolbar">
          <el-button type="danger" size="small" icon="el-icon-plus" @click="openCategoryDialog">新增分类</el-button>
          <el-input v-model="categorySearch" placeholder="搜索分类名称" prefix-icon="el-icon-search" size="small" style="width: 220px;" clearable @clear="fetchCategories" />
        </div>

        <el-table :data="categoryList" style="width: 100%;" border stripe v-loading="categoryLoading">
          <el-table-column prop="id" label="分类ID" width="100" />
          <el-table-column prop="name" label="分类名称" width="150" />
          <el-table-column label="图标" width="100">
            <template slot-scope="scope">
              <i :class="scope.row.icon" style="font-size: 20px;" />
            </template>
          </el-table-column>
          <el-table-column prop="count" label="商品数量" width="120" />
          <el-table-column prop="sort" label="排序值" width="100" />
          <el-table-column label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === '启用' ? 'success' : 'info'" size="small">{{ scope.row.status }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="创建时间" width="180">
            <template slot-scope="scope">{{ scope.row.createTime | formatDateTime }}</template>
          </el-table-column>
          <el-table-column label="操作" width="150" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="handleEditCategory(scope.row)">编辑</el-button>
              <el-button type="text" size="small" @click="handleToggleCategory(scope.row)">{{ scope.row.status === '启用' ? '禁用' : '启用' }}</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <!-- 新增/编辑分类对话框 -->
    <el-dialog :title="categoryDialogTitle" :visible.sync="categoryDialogVisible" width="450px">
      <el-form ref="categoryForm" :model="categoryForm" :rules="categoryRules" label-width="80px">
        <el-form-item label="分类名称" prop="name">
          <el-input v-model="categoryForm.name" placeholder="请输入分类名称" />
        </el-form-item>
        <el-form-item label="图标">
          <el-input v-model="categoryForm.icon" placeholder="Element图标名，如 el-icon-goods" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="categoryForm.sort" :min="0" :max="999" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="categoryForm.statusBool" active-text="启用" inactive-text="禁用" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="categoryDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="submitCategory">确定</el-button>
      </div>
    </el-dialog>

    <!-- 新增/编辑商品对话框 -->
    <el-dialog :title="productDialogTitle" :visible.sync="productDialogVisible" width="800px" :close-on-click-modal="false" @closed="resetProductForm">
      <el-form ref="productForm" :model="productForm" :rules="productRules" label-width="90px">
        <!-- 基本信息 -->
        <el-divider content-position="left">基本信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="商品名称" prop="goods_name">
              <el-input v-model="productForm.goods_name" placeholder="请输入商品名称" maxlength="200" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="所属分类" prop="cate_id">
              <el-select v-model="productForm.cate_id" placeholder="请选择分类" style="width: 100%;">
                <el-option v-for="c in categoryOptions" :key="c.id" :label="c.name" :value="c.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 多SKU管理 -->
        <el-divider content-position="left">
          <el-checkbox v-model="skuEnabled" style="margin-right: 8px;">启用多SKU规格</el-checkbox>
        </el-divider>
        <div v-if="skuEnabled" class="sku-section">
          <el-row :gutter="20" style="margin-bottom: 12px;">
            <el-col :span="12">
              <el-input v-model="skuSpecName1" placeholder="规格1名称，如：颜色" size="small">
                <template slot="prepend">规格1</template>
              </el-input>
              <el-input
                v-model="skuSpecValues1"
                placeholder="规格值，逗号分隔，如：红色,蓝色"
                size="small"
                style="margin-top: 6px;"
              />
            </el-col>
            <el-col :span="12">
              <el-input v-model="skuSpecName2" placeholder="规格2名称，如：尺寸" size="small">
                <template slot="prepend">规格2</template>
              </el-input>
              <el-input
                v-model="skuSpecValues2"
                placeholder="规格值，逗号分隔，如：S,M"
                size="small"
                style="margin-top: 6px;"
              />
            </el-col>
          </el-row>
          <el-button type="primary" size="small" plain @click="generateSkuList">生成SKU列表</el-button>

          <!-- SKU列表表格 -->
          <div v-if="productForm.skus.length > 0" style="margin-top: 12px;">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
              <span style="font-size: 13px; color: #606266;">SKU列表（共 {{ productForm.skus.length }} 条）</span>
              <el-button type="text" size="small" icon="el-icon-plus" @click="addSkuRow">添加SKU</el-button>
            </div>
            <el-table :data="productForm.skus" border size="small">
              <el-table-column label="规格组合" min-width="160">
                <template slot-scope="scope">
                  <el-input v-model="scope.row.spec_name_text" placeholder="规格名称" size="small" />
                </template>
              </el-table-column>
              <el-table-column label="成本价" width="130">
                <template slot-scope="scope">
                  <el-input-number v-model="scope.row.cost_price" :min="0" :precision="2" size="small" controls-position="right" style="width: 100%;" />
                </template>
              </el-table-column>
              <el-table-column label="售价" width="130">
                <template slot-scope="scope">
                  <el-input-number v-model="scope.row.price" :min="0" :precision="2" size="small" controls-position="right" style="width: 100%;" />
                </template>
              </el-table-column>
              <el-table-column label="原价" width="130">
                <template slot-scope="scope">
                  <el-input-number v-model="scope.row.market_price" :min="0" :precision="2" size="small" controls-position="right" style="width: 100%;" />
                </template>
              </el-table-column>
              <el-table-column label="库存" width="120">
                <template slot-scope="scope">
                  <el-input-number v-model="scope.row.stock" :min="0" size="small" controls-position="right" style="width: 100%;" />
                </template>
              </el-table-column>
              <el-table-column label="操作" width="60" fixed="right">
                <template slot-scope="scope">
                  <el-button type="text" size="small" style="color: #E4393C;" @click="removeSkuRow(scope.$index)" :disabled="productForm.skus.length <= 1">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
        <div v-else class="single-sku-section">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="成本价">
                <el-input-number v-model="productForm.skus[0].cost_price" :min="0" :precision="2" controls-position="right" style="width: 100%;" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="售价">
                <el-input-number v-model="productForm.skus[0].price" :min="0" :precision="2" controls-position="right" style="width: 100%;" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="原价">
                <el-input-number v-model="productForm.skus[0].market_price" :min="0" :precision="2" controls-position="right" style="width: 100%;" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="库存">
                <el-input-number v-model="productForm.skus[0].stock" :min="0" controls-position="right" style="width: 100%;" />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- 商品图片 -->
        <el-divider content-position="left">商品图片</el-divider>
        <el-form-item label="商品主图" prop="goods_img" required>
          <div class="upload-img-section">
            <el-upload
              class="main-img-uploader"
              action="https://api-lefeng365.linhebj.top/api/v1/manger/upload"
              :headers="uploadHeaders"
              :show-file-list="false"
              :on-success="handleMainImgSuccess"
              :before-upload="beforeUpload"
              accept="image/*"
            >
              <img v-if="productForm.goods_img" :src="productForm.goods_img" class="main-img-preview" />
              <i v-else class="el-icon-plus main-img-uploader-icon"></i>
            </el-upload>
            <span class="upload-tip">建议尺寸 800×800px，大小不超过5MB</span>
          </div>
        </el-form-item>
        <el-form-item label="轮播图">
          <div class="slide-imgs-section">
            <div v-for="(url, idx) in slideImgList" :key="idx" class="slide-img-item">
              <img :src="url" class="slide-img-preview" />
              <div class="slide-img-actions">
                <i class="el-icon-delete" @click="removeSlideImg(idx)"></i>
              </div>
            </div>
            <el-upload
              v-if="slideImgList.length < 9"
              class="slide-img-uploader"
              action="https://api-lefeng365.linhebj.top/api/v1/manger/upload"
              :headers="uploadHeaders"
              :show-file-list="false"
              :on-success="handleSlideImgSuccess"
              :before-upload="beforeUpload"
              accept="image/*"
            >
              <i class="el-icon-plus slide-img-uploader-icon"></i>
            </el-upload>
          </div>
          <span class="upload-tip">可上传多张，建议尺寸 800×800px</span>
        </el-form-item>

        <!-- 商品详情 -->
        <el-divider content-position="left">商品详情</el-divider>
        <el-form-item label="详情图片">
          <div class="detail-imgs-section">
            <div v-for="(url, idx) in detailImgList" :key="idx" class="detail-img-item">
              <img :src="url" class="detail-img-preview" />
              <div class="detail-img-actions">
                <i class="el-icon-delete" @click="removeDetailImg(idx)"></i>
              </div>
            </div>
            <el-upload
              v-if="detailImgList.length < 20"
              class="detail-img-uploader"
              action="https://api-lefeng365.linhebj.top/api/v1/manger/upload"
              :headers="uploadHeaders"
              :show-file-list="false"
              :on-success="handleDetailImgSuccess"
              :before-upload="beforeUpload"
              accept="image/*"
            >
              <i class="el-icon-plus detail-img-uploader-icon"></i>
            </el-upload>
          </div>
          <span class="upload-tip">支持多张详情图片，建议宽度750px</span>
        </el-form-item>

        <el-form-item label="上架状态" style="margin-top: 16px;">
          <el-switch v-model="productForm.status" :active-value="1" :inactive-value="0" active-text="上架" inactive-text="下架" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="productDialogVisible = false">取消</el-button>
        <el-button type="danger" :loading="productSubmitting" @click="submitProduct">确定</el-button>
      </div>
    </el-dialog>

    <!-- 虚拟销量调整弹窗 -->
    <el-dialog title="调整虚拟销量" :visible.sync="virtualSalesDialog" width="600px">
      <div v-loading="skuLoading">
        <p style="margin-bottom: 15px; color: #909399;">商品：{{ currentGoodName }}</p>
        <el-table :data="skuList" border>
          <el-table-column label="SKU规格" prop="spec_name_text" />
          <el-table-column label="售价" width="100">
            <template slot-scope="scope">¥{{ scope.row.price }}</template>
          </el-table-column>
          <el-table-column label="真实销量" width="90" prop="sales" />
          <el-table-column label="虚拟销量" width="160">
            <template slot-scope="scope">
              <el-input-number
                v-model="scope.row.virtual_sales"
                :min="0"
                :max="999999"
                size="mini"
                controls-position="right"
                @change="(val) => handleVirtualSalesChange(scope.row, val)"
              />
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="virtualSalesDialog = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getProducts, getProductDetail, createProduct, updateProduct, toggleProductStatus, batchProductOn, batchProductOff, batchProductDestroy, getCategories, createCategory, updateCategory, toggleCategoryStatus, getProductSkus, updateProductVirtualSales } from '@/api'

export default {
  name: 'Products',
  data() {
    return {
      activeTab: 'list',
      searchKeyword: '',
      filterCateId: '',
      selectAll: false,
      selectedIds: [],
      productList: [],
      productTotal: 0,
      productPage: 1,
      productLimit: 10,
      productLoading: false,
      virtualSalesDialog: false,
      currentGoodName: '',
      skuList: [],
      skuLoading: false,
      categorySearch: '',
      categoryList: [],
      categoryTotal: 0,
      categoryEnabledCount: 0,
      categoryLoading: false,
      categoryOptions: [],
      categoryDialogVisible: false,
      categoryDialogTitle: '新增分类',
      categoryForm: { id: null, name: '', icon: 'el-icon-goods', sort: 0, statusBool: true },
      categoryRules: {
        name: [{ required: true, message: '请输入分类名称', trigger: 'blur' }]
      },
      // 商品对话框
      productDialogVisible: false,
      productDialogTitle: '新增商品',
      productSubmitting: false,
      isEditing: false,
      editingId: null,
      productForm: {
        goods_name: '',
        cate_id: '',
        goods_img: '',
        goods_slide: '',
        detail_images: '',
        content: '',
        status: 1,
        skus: [{ spec_name_text: '默认规格', cost_price: 0, price: 0, market_price: 0, stock: 0 }]
      },
      productRules: {
        goods_name: [{ required: true, message: '请输入商品名称', trigger: 'blur' }],
        cate_id: [{ required: true, message: '请选择分类', trigger: 'change' }],
        goods_img: [{ required: true, message: '请上传商品主图', trigger: 'blur' }]
      },
      skuEnabled: false,
      skuSpecName1: '',
      skuSpecValues1: '',
      skuSpecName2: '',
      skuSpecValues2: ''
    }
  },
  computed: {
    uploadHeaders() {
      const token = localStorage.getItem('admin_token')
      return token ? { Authorization: `Bearer ${token}` } : {}
    },
    slideImgList() {
      const src = this.productForm.goods_slide
      if (!src) return []
      if (Array.isArray(src)) return src
      return src.split(',').filter(u => u.trim())
    },
    detailImgList() {
      const src = this.productForm.detail_images
      if (!src) return []
      if (Array.isArray(src)) return src
      return src.split(',').filter(u => u.trim())
    }
  },
  mounted() {
    this.fetchProducts()
    this.fetchCategoryOptions()
  },
  methods: {
    handleTabChange(tab) {
      if (tab.name === 'categories') {
        this.fetchCategories()
      }
    },
    // ========== 商品列表 ==========
    fetchProducts() {
      this.productLoading = true
      const params = {
        page: this.productPage,
        limit: this.productLimit
      }
      if (this.searchKeyword) params.keyword = this.searchKeyword
      if (this.filterCateId) params.cate_id = this.filterCateId

      getProducts(params).then(res => {
        this.productList = res.list || []
        this.productTotal = res.total || 0
      }).catch(() => {
        this.$message.error('获取商品列表失败')
      }).finally(() => {
        this.productLoading = false
      })
    },
    handleSelectionChange(val) {
      this.selectedIds = val.map(v => v.id)
    },
    handleSelectAll(val) {
      if (val) {
        this.$refs.productTable.toggleAllSelection()
      } else {
        this.$refs.productTable.clearSelection()
      }
    },
    batchAction(action) {
      const actionMap = { on: '上架', off: '下架', destroy: '删除' }
      this.$confirm(`确认批量${actionMap[action]}选中商品？`, '提示', { type: 'warning' }).then(() => {
        const ids = this.selectedIds
        let apiCall
        if (action === 'on') apiCall = batchProductOn({ ids })
        else if (action === 'off') apiCall = batchProductOff({ ids })
        else apiCall = batchProductDestroy({ ids })

        apiCall.then(() => {
          this.$message.success(`已批量${actionMap[action]}`)
          this.selectedIds = []
          this.fetchProducts()
        }).catch(() => {
          this.$message.error(`批量${actionMap[action]}失败`)
        })
      }).catch(() => {})
    },
    handleToggleStatus(row) {
      const action = row.status === '已上架' ? '下架' : '上架'
      this.$confirm(`确认${action}该商品？`, '提示', { type: 'warning' }).then(() => {
        toggleProductStatus({ id: row.id }).then(() => {
          this.$message.success(`${action}成功`)
          this.fetchProducts()
        }).catch(() => {
          this.$message.error(`${action}失败`)
        })
      }).catch(() => {})
    },
    openAddDialog() {
      this.isEditing = false
      this.editingId = null
      this.productDialogTitle = '新增商品'
      this.resetProductForm()
      this.productDialogVisible = true
    },
    handleBatchUpload() { this.$message.info('批量上传功能') },
    handleEdit(row) {
      this.isEditing = true
      this.editingId = row.id
      this.productDialogTitle = '编辑商品'
      this.productLoading = true
      getProductDetail(row.id).then(res => {
        const detail = res
        // 从 content 中提取详情图片
        const detailImages = this.extractDetailImages(detail.content || '')
        const skus = (detail.skus && detail.skus.length > 0) ? detail.skus.map(s => ({
          id: s.id,
          spec_name_text: s.spec_name_text || '默认规格',
          cost_price: Number(s.cost_price) || 0,
          price: Number(s.price) || 0,
          market_price: Number(s.market_price) || 0,
          stock: Number(s.stock) || 0
        })) : [{ spec_name_text: '默认规格', cost_price: 0, price: 0, market_price: 0, stock: 0 }]

        this.productForm = {
          goods_name: detail.goods_name || '',
          cate_id: detail.cate_id || '',
          goods_img: detail.goods_img || '',
          goods_slide: detail.goods_slide || '',
          detail_images: detailImages.join(','),
          content: '',
          status: detail.status !== undefined ? detail.status : 1,
          skus: skus
        }
        // 回显多SKU规格信息
        this.restoreSkuSpecInfo(skus)
        this.productDialogVisible = true
      }).catch(() => {
        this.$message.error('获取商品详情失败')
      }).finally(() => {
        this.productLoading = false
      })
    },
    // 根据已有SKU数据回显规格名称和规格值
    restoreSkuSpecInfo(skus) {
      this.skuEnabled = skus.length > 1
      this.skuSpecName1 = ''
      this.skuSpecValues1 = ''
      this.skuSpecName2 = ''
      this.skuSpecValues2 = ''

      if (skus.length <= 1) return

      // 解析所有SKU的规格组合文本，如 "颜色:红色 尺寸:S"
      const specTexts = skus.map(s => s.spec_name_text || '')
      const specParts = specTexts.map(t => {
        // 按空格分割出不同规格维度
        const parts = t.split(/\s+/).filter(p => p)
        const dims = {}
        parts.forEach(p => {
          const colonIdx = p.indexOf(':')
          if (colonIdx > 0) {
            dims[p.substring(0, colonIdx)] = p.substring(colonIdx + 1)
          }
        })
        return dims
      })

      // 收集所有规格名和对应的值
      const allDimNames = []
      const allDimValues = {}
      specParts.forEach(dim => {
        Object.keys(dim).forEach(name => {
          if (!allDimNames.includes(name)) {
            allDimNames.push(name)
          }
          if (!allDimValues[name]) {
            allDimValues[name] = new Set()
          }
          allDimValues[name].add(dim[name])
        })
      })

      // 回填规格1和规格2
      if (allDimNames.length >= 1) {
        this.skuSpecName1 = allDimNames[0]
        this.skuSpecValues1 = [...allDimValues[allDimNames[0]]].join(',')
      }
      if (allDimNames.length >= 2) {
        this.skuSpecName2 = allDimNames[1]
        this.skuSpecValues2 = [...allDimValues[allDimNames[1]]].join(',')
      }
    },
    // 从HTML content中提取img标签的src
    extractDetailImages(content) {
      const imgs = []
      const regex = /<img[^>]+src="([^">]+)"/g
      let match
      while ((match = regex.exec(content)) !== null) {
        imgs.push(match[1])
      }
      return imgs
    },

    // ========== 商品表单 ==========
    resetProductForm() {
      this.productForm = {
        goods_name: '',
        cate_id: '',
        goods_img: '',
        goods_slide: '',
        detail_images: '',
        content: '',
        status: 1,
        skus: [{ spec_name_text: '默认规格', cost_price: 0, price: 0, market_price: 0, stock: 0 }]
      }
      this.skuEnabled = false
      this.skuSpecName1 = ''
      this.skuSpecValues1 = ''
      this.skuSpecName2 = ''
      this.skuSpecValues2 = ''
      this.isEditing = false
      this.editingId = null
      if (this.$refs.productForm) {
        this.$refs.productForm.resetFields()
      }
    },
    generateSkuList() {
      const values1 = this.skuSpecValues1.split(',').map(v => v.trim()).filter(v => v)
      const values2 = this.skuSpecValues2.split(',').map(v => v.trim()).filter(v => v)
      const name1 = this.skuSpecName1.trim()
      const name2 = this.skuSpecName2.trim()

      if (values1.length === 0) {
        this.$message.warning('请输入规格1的值')
        return
      }

      // 收集已有SKU中保存的价格/库存数据，按spec_name_text索引
      const existingDataMap = {}
      this.productForm.skus.forEach(s => {
        if (s.spec_name_text && (s.price > 0 || s.market_price > 0 || s.stock > 0 || s.cost_price > 0)) {
          existingDataMap[s.spec_name_text] = {
            cost_price: s.cost_price,
            price: s.price,
            market_price: s.market_price,
            stock: s.stock
          }
        }
      })

      const skus = []
      if (values2.length > 0) {
        values1.forEach(v1 => {
          values2.forEach(v2 => {
            const specText = `${name1 ? name1 + ':' : ''}${v1} ${name2 ? name2 + ':' : ''}${v2}`
            const existing = existingDataMap[specText]
            skus.push({
              spec_name_text: specText,
              cost_price: existing ? existing.cost_price : 0,
              price: existing ? existing.price : 0,
              market_price: existing ? existing.market_price : 0,
              stock: existing ? existing.stock : 0
            })
          })
        })
      } else {
        values1.forEach(v1 => {
          const specText = `${name1 ? name1 + ':' : ''}${v1}`
          const existing = existingDataMap[specText]
          skus.push({
            spec_name_text: specText,
            cost_price: existing ? existing.cost_price : 0,
            price: existing ? existing.price : 0,
            market_price: existing ? existing.market_price : 0,
            stock: existing ? existing.stock : 0
          })
        })
      }
      this.productForm.skus = skus
    },
    // 手动添加单条SKU
    addSkuRow() {
      this.productForm.skus.push({
        spec_name_text: '',
        cost_price: 0,
        price: 0,
        market_price: 0,
        stock: 0
      })
    },
    // 删除单条SKU
    removeSkuRow(index) {
      if (this.productForm.skus.length <= 1) {
        this.$message.warning('至少保留一条SKU')
        return
      }
      this.productForm.skus.splice(index, 1)
      // 如果删除后只剩一条，自动关闭多SKU模式
      if (this.productForm.skus.length === 1) {
        this.skuEnabled = false
      }
    },
    handleMainImgSuccess(response) {
      if (response.result === 'ok' && response.message) {
        this.productForm.goods_img = response.message
      }
    },
    handleSlideImgSuccess(response) {
      if (response.result === 'ok' && response.message) {
        const current = this.productForm.goods_slide
          ? this.productForm.goods_slide.split(',').filter(u => u.trim())
          : []
        current.push(response.message)
        this.productForm.goods_slide = current.join(',')
      }
    },
    removeSlideImg(idx) {
      const current = this.productForm.goods_slide.split(',').filter(u => u.trim())
      current.splice(idx, 1)
      this.productForm.goods_slide = current.join(',')
    },
    handleDetailImgSuccess(response) {
      if (response.result === 'ok' && response.message) {
        const current = this.productForm.detail_images
          ? this.productForm.detail_images.split(',').filter(u => u.trim())
          : []
        current.push(response.message)
        this.productForm.detail_images = current.join(',')
      }
    },
    removeDetailImg(idx) {
      const current = this.productForm.detail_images.split(',').filter(u => u.trim())
      current.splice(idx, 1)
      this.productForm.detail_images = current.join(',')
    },
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        this.$message.error('只能上传图片文件')
        return false
      }
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB')
        return false
      }
      return true
    },
    submitProduct() {
      this.$refs.productForm.validate(valid => {
        if (!valid) return

        // 验证 SKU
        const validSkus = this.productForm.skus.every(s => s.price >= 0 && s.market_price >= 0 && s.stock >= 0)
        if (!validSkus) {
          this.$message.error('请完善SKU的价格和库存信息')
          return
        }

        this.productSubmitting = true
        const data = {
          cate_id: this.productForm.cate_id,
          goods_name: this.productForm.goods_name,
          goods_img: this.productForm.goods_img,
          goods_slide: this.productForm.goods_slide,
          detail_images: this.productForm.detail_images,
          content: this.productForm.content,
          status: this.productForm.status,
          skus: this.productForm.skus.map(s => ({
            spec_name_text: s.spec_name_text || '默认规格',
            price: s.price,
            market_price: s.market_price,
            cost_price: s.cost_price || 0,
            stock: s.stock
          }))
        }

        const apiCall = this.isEditing
          ? updateProduct(this.editingId, data)
          : createProduct(data)

        apiCall.then(() => {
          this.$message.success(this.isEditing ? '更新成功' : '创建成功')
          this.productDialogVisible = false
          this.fetchProducts()
        }).catch(() => {
          this.$message.error(this.isEditing ? '更新失败' : '创建失败')
        }).finally(() => {
          this.productSubmitting = false
        })
      })
    },

    // ========== 分类管理 ==========
    fetchCategoryOptions() {
      getCategories().then(res => {
        this.categoryOptions = res.list || []
      }).catch(() => {})
    },
    fetchCategories() {
      this.categoryLoading = true
      const params = {}
      if (this.categorySearch) params.keyword = this.categorySearch
      getCategories(params).then(res => {
        this.categoryList = res.list || []
        this.categoryTotal = res.total || 0
        this.categoryEnabledCount = this.categoryList.filter(c => c.status === '启用').length
      }).catch(() => {
        this.$message.error('获取分类列表失败')
      }).finally(() => {
        this.categoryLoading = false
      })
    },
    openCategoryDialog(row) {
      if (row) {
        this.categoryDialogTitle = '编辑分类'
        this.categoryForm = {
          id: row.id,
          name: row.name,
          icon: row.icon,
          sort: row.sort,
          statusBool: row.status === '启用'
        }
      } else {
        this.categoryDialogTitle = '新增分类'
        this.categoryForm = { id: null, name: '', icon: 'el-icon-goods', sort: 0, statusBool: true }
      }
      this.categoryDialogVisible = true
    },
    submitCategory() {
      this.$refs.categoryForm.validate(valid => {
        if (!valid) return
        const data = {
          name: this.categoryForm.name,
          icon: this.categoryForm.icon,
          sort: this.categoryForm.sort,
          status: this.categoryForm.statusBool ? 1 : 0
        }
        const apiCall = this.categoryForm.id
          ? updateCategory(this.categoryForm.id, data)
          : createCategory(data)

        apiCall.then(() => {
          this.$message.success(this.categoryForm.id ? '更新成功' : '创建成功')
          this.categoryDialogVisible = false
          this.fetchCategories()
          this.fetchCategoryOptions()
        }).catch(() => {
          this.$message.error('操作失败')
        })
      })
    },
    handleEditCategory(row) {
      this.openCategoryDialog(row)
    },
    handleToggleCategory(row) {
      toggleCategoryStatus(row.id).then(() => {
        this.$message.success('操作成功')
        this.fetchCategories()
      }).catch(() => {
        this.$message.error('操作失败')
      })
    },
    openVirtualSales(row) {
      this.currentGoodName = row.name
      this.virtualSalesDialog = true
      this.skuLoading = true
      getProductSkus(row.id).then(res => {
        this.skuList = res || []
        this.skuLoading = false
      }).catch(() => {
        this.skuLoading = false
      })
    },
    handleVirtualSalesChange(sku, val) {
      updateProductVirtualSales({
        sku_id: sku.id,
        virtual_sales: val
      }).then(() => {
        this.$message.success('虚拟销量已更新')
      })
    }
  }
}
</script>

<style scoped>
.products-tabs { border-radius: 8px; overflow: hidden; }
.toolbar { display: flex; align-items: center; justify-content: space-between; padding: 16px 0; gap: 12px; }
.toolbar-left { display: flex; gap: 12px; }
.toolbar-right { display: flex; gap: 12px; }
.batch-bar { display: flex; align-items: center; gap: 8px; padding: 12px 0; border-bottom: 1px solid #f0f0f0; }
.price-red { color: #E4393C; font-weight: bold; }
.price-original { color: #c0c4cc; text-decoration: line-through; }
.pagination-wrap { display: flex; align-items: center; justify-content: space-between; padding: 16px 0 0; font-size: 13px; color: #909399; }
.cat-stats { margin-bottom: 24px; }
.cat-stat-card { text-align: center; border-radius: 8px; }
.cat-stat-card.cat-stat-red { background: #fef0f0; border-color: #fde2e2; }
.cat-stat-card.cat-stat-green { background: #f0f9eb; border-color: #e1f3d8; }
.cat-stat-card.cat-stat-gray { background: #f5f7fa; border-color: #e4e7ed; }
.cat-stat-label { font-size: 13px; color: #909399; margin: 0 0 8px; }
.cat-stat-value { font-size: 28px; font-weight: bold; margin: 0; }
.cat-stat-red .cat-stat-value { color: #E4393C; }
.cat-stat-green .cat-stat-value { color: #67c23a; }
.cat-stat-gray .cat-stat-value { color: #606266; }
/* 商品表单样式 */
.upload-img-section { display: flex; flex-direction: column; gap: 8px; }
.main-img-uploader { border: 1px dashed #d9d9d9; border-radius: 6px; cursor: pointer; width: 120px; height: 120px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
.main-img-uploader:hover { border-color: #E4393C; }
.main-img-preview { width: 120px; height: 120px; object-fit: cover; }
.main-img-uploader-icon { font-size: 28px; color: #8c939d; width: 120px; height: 120px; line-height: 120px; text-align: center; }
.upload-tip { font-size: 12px; color: #909399; }
/* 轮播图 & 详情图 上传 */
.slide-imgs-section, .detail-imgs-section { display: flex; flex-wrap: wrap; gap: 8px; }
.slide-img-item, .detail-img-item { position: relative; width: 100px; height: 100px; border-radius: 6px; overflow: hidden; border: 1px solid #e4e7ed; }
.slide-img-preview, .detail-img-preview { width: 100px; height: 100px; object-fit: cover; }
.slide-img-actions, .detail-img-actions { position: absolute; top: 0; right: 0; width: 24px; height: 24px; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; cursor: pointer; border-radius: 0 6px 0 6px; }
.slide-img-actions i, .detail-img-actions i { color: #fff; font-size: 14px; }
.slide-img-uploader, .detail-img-uploader { border: 1px dashed #d9d9d9; border-radius: 6px; cursor: pointer; width: 100px; height: 100px; display: flex; align-items: center; justify-content: center; }
.slide-img-uploader:hover, .detail-img-uploader:hover { border-color: #E4393C; }
.slide-img-uploader-icon, .detail-img-uploader-icon { font-size: 28px; color: #8c939d; width: 100px; height: 100px; line-height: 100px; text-align: center; }
/* SKU区域 */
.sku-section { background: #f5f7fa; border-radius: 8px; padding: 16px; }
.single-sku-section { background: #f5f7fa; border-radius: 8px; padding: 16px; }
</style>
